import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, ElementRef, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, FormGroup, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDatepickerI18n,
  NgbDatepickerModule,
  NgbInputDatepickerConfig,
  NgbDateStruct,
  NgbDateParserFormatter
} from '@ng-bootstrap/ng-bootstrap';
import { FormInputError } from '../_model/form-input.model';
import { TranslateService } from '@ngx-translate/core';
import { NgbDatepickerI18nPolish } from './const/datepicker-translation.const';
import { TextMaskModule } from '@matheo/text-mask';
import { MaskConfig } from '../form-input/mask';
import { ValidationFunctions } from 'src/app/shared/validation/validation-functions.const';
import { NgbDateCustomParserFormatter } from './const/datepicker-formatter.const';

@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [NgbDatepickerModule, ReactiveFormsModule, CommonModule, FormsModule, TextMaskModule],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
  providers: [
    NgbInputDatepickerConfig,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerComponent),
      multi: true
    },
    { provide: NgbDatepickerI18n, useClass: NgbDatepickerI18nPolish },
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter }
  ]
})
export class DatepickerComponent implements ControlValueAccessor, AfterContentInit {
  @Input() formGroup: FormGroup;
  @Input() formControlName: string;
  @Input() id: string = 'dateInput';
  @Input() label: string;
  @Input() isRequired: Boolean;
  @Input() errorList: FormInputError[] = [];
  @Input() wrapSubLabel: boolean = false;
  @Input() subLabel: string = null;
  @Input() set minDate(date: Date) {
    if (date) {
      this.minDateStruct = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      };
    }
  };
  @Input() set maxDate(date: Date) {
    if (date) {
      this.maxDateStruct = {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate()
      }
    }
  };
  @Input() disabled: boolean;
  @Output() blured = new EventEmitter<Event>();
  MaskConfig = MaskConfig;

  dynamicAutocomplete = 'nope-' + Math.random().toString(36).substring(2);
  isReadonlyHack = true;

  onChange: (value) => void = () => { };
  onTouched: () => void = () => { };
  minDateStruct: NgbDateStruct
  maxDateStruct: NgbDateStruct

  constructor(config: NgbInputDatepickerConfig, calendar: NgbCalendar, private translate: TranslateService) {
    config.outsideDays = 'hidden';

    config.placement = ['top-start', 'bottom-start'];

    const minDate = new Date('1940-01-01');

    this.minDateStruct = {
      year: minDate.getFullYear(),
      month: minDate.getMonth() + 1,
      day: minDate.getDate()
    };

    const currentYear = new Date().getFullYear();
    this.maxDateStruct = {
      year: currentYear + 15,
      month: 12,
      day: 31
    };
  }

  removeReadonlyHack(): void {
    this.isReadonlyHack = false;
  }

  onHasOneOfErrors(errors: string[]) {
    let invalid = false;
    errors.forEach((value) => {
      if (this.formGroup?.controls[this.formControlName]?.hasError(value)) {
        invalid = true;
      }
    });
    return this.isTouched() && invalid;
  }

  ngAfterContentInit(): void {
    if (this.formGroup) {
      this.formGroup.get(this.formControlName).addValidators(ValidationFunctions.dateValidator());
    }

    this.errorList.push(
      { label: this.translate.instant('SHARED.DATEPICKER.wrongDate'), type: ['invalidDate'] }
    )
  }

  isTouched() {
    return this.formGroup?.controls[this.formControlName]?.touched;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  onPropagateBlur(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    const parsedDate = this.parseDate(inputValue);
    if (parsedDate) {
      this.formGroup?.get(this.formControlName)?.patchValue(parsedDate);
      this.onTouched();
      this.onChange(parsedDate);
      this.blured.emit(event)
    }
  }

  onDateSelect(): void {
    setTimeout(() => {
      const inputElement = document.getElementById(this.id) as HTMLInputElement;
      if (inputElement) {
        inputElement.dispatchEvent(new Event('blur'));
      }
    });
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (value) {
      const date = this.convertToNgbDate(value);
      this.onChange(date);
    }
  }

  private isBeforeMinDate(value: NgbDate): boolean {
    const minDateAsNgbDate = new NgbDate(this.minDateStruct.year, this.minDateStruct.month, this.minDateStruct.day);

    return value.before(minDateAsNgbDate);
  }

  registerOnChange(fn: any): void {
    this.onChange = (value: NgbDate | null): void => {
      if (value?.year && value?.month && value?.day) {
        const yearString = value.year.toString();

        if (yearString.length === 4) {
          const providedDate = new NgbDate(value.year, value.month, value.day);

          const isBeforeMinDate = this.isBeforeMinDate(providedDate);

          if (isBeforeMinDate) {
            providedDate.day = this.minDateStruct.day;
            providedDate.month = this.minDateStruct.month;
            providedDate.year = this.minDateStruct.year;
          }

          const formattedDate = `${String(providedDate.day).padStart(2, '0')}.${String(providedDate.month).padStart(2, '0')}.${providedDate.year}`;
          fn(formattedDate);
        } else {
          fn(null);
        }
      } else {
        fn(null);
      }
    };
  }

  convertToNgbDate(value: any): NgbDate {
    if (!value) return null;

    if (value instanceof Date) {
      return new NgbDate(value.getFullYear(), value.getMonth() + 1, value.getDate());
    }

    if (typeof value === 'string') {
      const parts = value.split('.');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          return new NgbDate(year, month, day);
        }
      }
    }

    const date = new Date(value);

    if (!isNaN(date.getTime())) {
      return new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    }

    return null;
  }

  onInputChange(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    const parsedDate = this.parseDate(inputValue);
    if (parsedDate && String(parsedDate.year).length === 4) {
      this.onChange(parsedDate);
    }
  }

  parseDate(value: string): NgbDateStruct | null {
    const dateParts = value?.split('.');
    if (dateParts.length === 3 && dateParts[2]?.length === 4) {
      const day = parseInt(dateParts[0].padStart(2, '0'), 10);
      const month = parseInt(dateParts[1].padStart(2, '0'), 10);
      const year = parseInt(dateParts[2], 10);

      if (day && month && year) {
        return { year, month, day };
      }
    }
    return null;
  }
}
